/**
 * Readers for the statistical-package formats the frozen Step 1 panel
 * names — SPSS .sav, Stata .dta, SAS .sas7bdat, SAS transport .xpt —
 * built on statfmt, the MIT-licensed TypeScript port of ReadStat
 * (the brief's recommended codec; see OPEN_QUESTIONS Q19 for the
 * substitution record). The codec is loaded lazily through a dynamic
 * import so the application bundle only pays for it when a user
 * actually opens one of these files.
 *
 * Platform note: every module in the codec is platform-neutral except
 * its .sav reader, which imports node:zlib and calls it ONLY for
 * zlib-compressed (.zsav) streams. Plain .sav files never touch it.
 * .zsav is outside the panel's format list and is rejected up front
 * (see read.ts); a future browser build that wants .zsav supplies a
 * zlib provider behind this same seam.
 *
 * Fidelity rules implemented here:
 * - variable labels, value labels, display formats, and measurement
 *   levels are carried onto the uniform table (brief DAT-02);
 * - declared user-missing codes/ranges become column metadata AND
 *   their cells normalize to null (brief ING-06); system-missing and
 *   Stata/SAS tagged missings (.a-.z, .A-.Z) already arrive as null
 *   from the codec — the specific tag letter is not retained, since
 *   the engine's contract is null-based;
 * - SAS value labels live in a separate format catalog (.sas7bcat),
 *   not in the data file, so SAS columns arrive with their format
 *   names and no label pairs (a catalog reader is a later block);
 * - date-formatted numerics keep the source encoding (SPSS seconds
 *   since 1582, Stata/SAS days since 1960) and are typed 'date' with
 *   the epoch recorded; nothing is silently converted.
 */
import { parseFailure } from './errors.js';
import { NO_USER_MISSING, isUserMissing, } from './types.js';
const FORMAT_LABELS = {
    sav: 'SPSS .sav',
    dta: 'Stata .dta',
    sas7bdat: 'SAS .sas7bdat',
    xpt: 'SAS transport .xpt',
};
let codecPromise = null;
function loadCodec() {
    codecPromise ??= import('@irbisadm/statfmt');
    return codecPromise;
}
// ReadStatType enum values (statfmt types.ts).
const STORAGE_TYPES = {
    0: 'string',
    1: 'int8',
    2: 'int16',
    3: 'int32',
    4: 'float',
    5: 'double',
    6: 'string', // STRING_REF (SPSS long strings)
};
const MEASURES = {
    0: 'unknown',
    1: 'nominal',
    2: 'ordinal',
    3: 'scale',
};
function dateEpochFor(format, displayFormat) {
    if (!displayFormat)
        return null;
    const f = displayFormat.toUpperCase().replace(/\.$/, '');
    if (format === 'sav') {
        if (f.includes('DATE') ||
            f.includes('DATETIME') ||
            f.startsWith('TIME') ||
            f.startsWith('DTIME')) {
            return 'spss-seconds';
        }
        return null;
    }
    if (format === 'dta') {
        // Only %td is a true day count; other %t formats are period
        // indexes or millisecond clocks and stay numeric.
        return f === '%TD' ? 'days-1960' : null;
    }
    // SAS formats (xpt and sas7bdat share the convention).
    if (f.startsWith('DATETIME') || f.startsWith('TIME')) {
        return 'seconds-1960';
    }
    if (f.startsWith('DATE') ||
        f.startsWith('MMDDYY') ||
        f.startsWith('DDMMYY') ||
        f.startsWith('YYMMDD') ||
        f.startsWith('JULIAN')) {
        return 'days-1960';
    }
    return null;
}
function userMissingFrom(ranges) {
    if (ranges.length === 0)
        return NO_USER_MISSING;
    const values = [];
    const out = [];
    for (const r of ranges) {
        if (r.lo === null || r.hi === null)
            continue;
        if (r.lo === r.hi)
            values.push(r.lo);
        else
            out.push({ lo: r.lo, hi: r.hi });
    }
    if (values.length === 0 && out.length === 0)
        return NO_USER_MISSING;
    return { values, ranges: out };
}
export async function readStatTable(bytes, format) {
    const codec = await loadCodec();
    let ds;
    try {
        // The codec names the transport format 'xport'.
        ds = codec.readData(format === 'xpt' ? 'xport' : format, bytes);
    }
    catch (cause) {
        throw parseFailure(FORMAT_LABELS[format], cause);
    }
    const columns = ds.variables.map((v) => {
        const epoch = dateEpochFor(format, v.format);
        const valueLabels = (v.valueLabels ?? [])
            .filter((p) => p.value !== null)
            .map((p) => ({ value: p.value, label: p.label }));
        return {
            name: v.name,
            label: v.label,
            type: epoch ? 'date' : v.type === 0 || v.type === 6 ? 'string' : 'number',
            storageType: STORAGE_TYPES[v.type] ?? null,
            displayFormat: v.format,
            measure: MEASURES[v.measure] ?? 'unknown',
            valueLabels,
            userMissing: userMissingFrom(v.missingRanges ?? []),
            ...(epoch ? { dateEpoch: epoch } : {}),
            provenance: 'embedded',
        };
    });
    const rows = ds.rows.map((row) => row.map((cell, i) => {
        if (cell === null)
            return null;
        const spec = columns[i].userMissing;
        if ((spec.values.length > 0 || spec.ranges.length > 0) &&
            isUserMissing(spec, cell)) {
            return null;
        }
        return cell;
    }));
    const table = {
        format,
        fileLabel: ds.metadata.fileLabel,
        columns,
        rows,
    };
    return { table, issues: [] };
}
//# sourceMappingURL=statfmt.js.map